import { defaultLocale, t } from '@/i18n/i18n';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import type { ErrorInfo } from 'react';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';

interface ErrorBoundaryFallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onReset: () => void;
  onGoHome: () => void;
}

/** Standalone t — ErrorBoundary sits above LanguageProvider in App, so hooks are unavailable. */
const translate = (key: string) => t(key, defaultLocale);

export function ErrorBoundaryFallback({
  error,
  errorInfo,
  onReset,
  onGoHome,
}: ErrorBoundaryFallbackProps) {
  return (
    <div className='bg-background flex min-h-screen items-center justify-center p-4'>
      <Card className='w-full max-w-2xl'>
        <CardHeader>
          <div className='flex items-center gap-2'>
            <AlertCircle className='text-destructive h-6 w-6' />
            <CardTitle>{translate('errors.boundary.title')}</CardTitle>
          </div>
          <CardDescription>{translate('errors.boundary.description')}</CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Alert variant='destructive'>
            <AlertCircle className='h-4 w-4' />
            <AlertTitle>{translate('errors.boundary.errorDetails')}</AlertTitle>
            <AlertDescription>
              <pre className='mt-2 max-h-40 overflow-auto text-xs'>
                {error?.message || translate('errors.boundary.unknownError')}
              </pre>
            </AlertDescription>
          </Alert>

          {import.meta.env.DEV && errorInfo && (
            <details className='cursor-pointer rounded-lg border p-4'>
              <summary className='font-semibold'>{translate('errors.boundary.stackTrace')}</summary>
              <pre className='text-muted-foreground mt-2 max-h-60 overflow-auto text-xs'>
                {errorInfo.componentStack}
              </pre>
            </details>
          )}

          <div className='bg-muted rounded-lg p-4'>
            <h4 className='mb-2 font-semibold'>{translate('errors.boundary.whatYouCanDo')}</h4>
            <ul className='text-muted-foreground list-disc space-y-1 pl-5 text-sm'>
              <li>{translate('errors.boundary.refreshPage')}</li>
              <li>{translate('errors.boundary.goHomeHint')}</li>
              <li>{translate('errors.boundary.clearCache')}</li>
              <li>{translate('errors.boundary.contactSupport')}</li>
            </ul>
          </div>
        </CardContent>
        <CardFooter className='flex gap-2'>
          <Button onClick={onReset} variant='default' className='flex items-center gap-2'>
            <RefreshCw className='h-4 w-4' />
            {translate('errors.boundary.tryAgain')}
          </Button>
          <Button onClick={onGoHome} variant='outline' className='flex items-center gap-2'>
            <Home className='h-4 w-4' />
            {translate('errors.boundary.goHome')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
